import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, ChevronRight, Package, TrendingUp, Users, Target, Award, Heart, Shield } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Homepage = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [topBrands, setTopBrands] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchProducts();
    fetchBrands();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`, { params: { limit: 6 } });
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchBrands = async () => {
    try {
      const [brandsRes, productsRes, featuredRes] = await Promise.all([
        axios.get(`${API}/products/brands`),
        axios.get(`${API}/products`, { params: { limit: 5000 } }),
        axios.get(`${API}/featured-brands`)
      ]);
      
      setBrands(brandsRes.data.brands);
      
      // Count products per brand
      const brandCounts = {};
      productsRes.data.forEach(p => {
        brandCounts[p.brand] = (brandCounts[p.brand] || 0) + 1;
      });
      
      // Use featured brands if available, otherwise use top 12 by product count
      if (featuredRes.data && featuredRes.data.length > 0) {
        // Use manually curated featured brands
        const featured = featuredRes.data.map(fb => ({
          brand: fb.brand_name,
          count: brandCounts[fb.brand_name] || 0
        }));
        setTopBrands(featured);
      } else {
        // Fallback to automatic top 12 brands by product count
        const sorted = Object.entries(brandCounts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 12)
          .map(([brand, count]) => ({ brand, count }));
        setTopBrands(sorted);
      }
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  const handleSearch = () => {
    if (searchTerm.trim()) {
      navigate(`/catalog?search=${searchTerm}`);
    } else {
      navigate('/catalog');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white shadow-md z-50 backdrop-blur-sm bg-opacity-95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
              <img src="/saifoodhouse-logo.png" alt="Sai Food House" className="h-14" />
            </div>
            <div className="hidden md:flex space-x-8">
              <a href="/" className="text-[#0b1f3f] hover:text-[#fcce32] font-semibold transition-colors" data-testid="nav-home">Home</a>
              <a href="/catalog" className="text-gray-700 hover:text-[#fcce32] font-semibold transition-colors" data-testid="nav-catalog">Products</a>
              <a href="/about" className="text-gray-700 hover:text-[#fcce32] font-semibold transition-colors" data-testid="nav-about">About Us</a>
              <a href="/contact" className="text-gray-700 hover:text-[#fcce32] font-semibold transition-colors" data-testid="nav-contact">Contact</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-20 min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src="/warehouse.jpg" alt="Warehouse" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0b1f3f]/95 via-[#0b1f3f]/90 to-[#0b1f3f]/80" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-white space-y-8 animate-fade-in">
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight" data-testid="hero-heading">
                Your Trusted Wholesale Partner
              </h1>
              <p className="text-xl sm:text-2xl text-gray-200 leading-relaxed" data-testid="hero-subheading">
                Specializing in food and beverage distribution across India. We connect trusted brands with retailers, restaurants, and institutions.
              </p>
              
              {/* Search Bar */}
              <div className="max-w-xl">
                <div className="relative flex gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      type="text"
                      placeholder="Search by brand or product name..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                      className="pl-12 h-14 text-lg bg-white text-gray-800 border-none shadow-xl rounded-xl"
                      data-testid="search-input"
                    />
                  </div>
                  <Button
                    onClick={handleSearch}
                    className="h-14 px-8 bg-[#fcce32] text-[#0b1f3f] hover:bg-[#e0b82a] font-bold rounded-xl shadow-xl"
                    data-testid="search-button"
                  >
                    Search
                  </Button>
                </div>
              </div>

              <div className="flex gap-4">
                <Button
                  onClick={() => navigate('/catalog')}
                  className="h-14 px-8 bg-[#fcce32] text-[#0b1f3f] hover:bg-[#e0b82a] text-lg font-bold rounded-xl shadow-2xl"
                  data-testid="explore-products-btn"
                >
                  Explore Products
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  onClick={() => navigate('/about')}
                  variant="outline"
                  className="h-14 px-8 bg-white/10 border-2 border-white text-white hover:bg-white hover:text-[#0b1f3f] text-lg font-bold rounded-xl backdrop-blur-sm"
                >
                  Learn More
                </Button>
              </div>
            </div>

            <div className="hidden lg:block relative">
              <div className="absolute inset-0 bg-[#fcce32]/20 rounded-full blur-3xl" />
              <img src="/brand-showcase.png" alt="Brands" className="relative rounded-2xl shadow-2xl animate-float" />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-r from-[#0b1f3f] to-[#1a3a5c]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center text-white">
              <Package className="w-12 h-12 mx-auto mb-3 text-[#fcce32]" />
              <div className="text-4xl font-bold mb-2">1200+</div>
              <div className="text-gray-300">Products</div>
            </div>
            <div className="text-center text-white">
              <TrendingUp className="w-12 h-12 mx-auto mb-3 text-[#fcce32]" />
              <div className="text-4xl font-bold mb-2">{brands.length}+</div>
              <div className="text-gray-300">Trusted Brands</div>
            </div>
            <div className="text-center text-white">
              <Users className="w-12 h-12 mx-auto mb-3 text-[#fcce32]" />
              <div className="text-4xl font-bold mb-2">500+</div>
              <div className="text-gray-300">Happy Clients</div>
            </div>
            <div className="text-center text-white">
              <Target className="w-12 h-12 mx-auto mb-3 text-[#fcce32]" />
              <div className="text-4xl font-bold mb-2">Pan India</div>
              <div className="text-gray-300">Distribution</div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-[#0b1f3f] mb-4">Why Choose Sai Food House?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Your success is our priority. We deliver excellence at every step.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-shadow p-8 text-center">
              <div className="w-20 h-20 bg-[#fcce32] bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Shield className="w-10 h-10 text-[#fcce32]" />
              </div>
              <h3 className="text-2xl font-bold text-[#0b1f3f] mb-4">Quality Assurance</h3>
              <p className="text-gray-600 leading-relaxed">
                We maintain strict quality standards and partner only with certified brands and suppliers who meet our rigorous quality requirements.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-shadow p-8 text-center">
              <div className="w-20 h-20 bg-[#fcce32] bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Package className="w-10 h-10 text-[#fcce32]" />
              </div>
              <h3 className="text-2xl font-bold text-[#0b1f3f] mb-4">Reliable Distribution</h3>
              <p className="text-gray-600 leading-relaxed">
                Our extensive distribution network ensures timely delivery of products to your business location with complete transparency and tracking.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-shadow p-8 text-center">
              <div className="w-20 h-20 bg-[#fcce32] bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart className="w-10 h-10 text-[#fcce32]" />
              </div>
              <h3 className="text-2xl font-bold text-[#0b1f3f] mb-4">Customer Success</h3>
              <p className="text-gray-600 leading-relaxed">
                Dedicated support team and flexible ordering solutions designed to help your business grow and succeed in the competitive market.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Top Brands Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-[#0b1f3f] mb-4">Famous Brands in Our Catalog</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Discover top-quality products from India's most trusted brands. From household names to emerging favorites, find everything you need for your business.
            </p>
          </div>

          <div className="mb-12">
            <h3 className="text-2xl font-bold text-center text-gray-800 mb-8">Top Performing Brands</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {topBrands.map((item, index) => (
                <div
                  key={item.brand}
                  className="bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-md hover:shadow-xl transition-all p-6 border-2 border-gray-100 hover:border-[#fcce32] cursor-pointer group"
                  onClick={() => navigate(`/catalog?brand=${item.brand}`)}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="text-center">
                    <div className="w-16 h-16 bg-[#0b1f3f] rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                      <span className="text-2xl font-bold text-[#fcce32]">{item.brand.charAt(0)}</span>
                    </div>
                    <h4 className="text-lg font-bold text-gray-800 mb-2 group-hover:text-[#fcce32] transition-colors">{item.brand}</h4>
                    <p className="text-sm text-gray-600">{item.count} Products</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center">
            <Button
              onClick={() => navigate('/catalog')}
              className="h-12 px-8 bg-[#0b1f3f] text-white hover:bg-[#1a3a5c] font-semibold rounded-xl"
            >
              View All Brands
              <ChevronRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Serving Businesses Section */}
      <section className="py-20 bg-[#0b1f3f] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-bold mb-6">Serving Businesses Across India</h2>
            <p className="text-xl text-gray-200 max-w-4xl mx-auto leading-relaxed">
              From neighborhood grocery stores to large restaurant chains and institutional kitchens, Sai Food House provides comprehensive wholesale solutions tailored to meet diverse business requirements and scale with your growth.
            </p>
          </div>
          <div className="text-center mt-12">
            <Button
              onClick={() => navigate('/about')}
              className="h-14 px-10 bg-[#fcce32] text-[#0b1f3f] hover:bg-[#e0b82a] text-lg font-bold rounded-xl shadow-xl"
            >
              Learn More About Us
              <ChevronRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#fcce32] to-[#e0b82a]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-[#0b1f3f] mb-6">
            Don't See Your Preferred Brand?
          </h2>
          <p className="text-xl text-gray-800 mb-8">
            We're constantly expanding our catalog. Contact us to request specific brands or products for your business needs.
          </p>
          <div className="flex gap-4 justify-center">
            <Button
              onClick={() => navigate('/catalog')}
              className="h-12 px-8 bg-[#0b1f3f] text-white hover:bg-[#1a3a5c] font-semibold rounded-xl"
            >
              Browse Catalog
            </Button>
            <Button
              onClick={() => navigate('/contact')}
              variant="outline"
              className="h-12 px-8 bg-white text-[#0b1f3f] border-2 border-[#0b1f3f] hover:bg-gray-50 font-semibold rounded-xl"
            >
              Request a Brand
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0b1f3f] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <img src="/saifoodhouse-logo.png" alt="Sai Food House" className="h-12 mb-4" />
              <p className="text-gray-300">
                Your trusted distributor of quality food & beverage products across India.
              </p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4 text-[#fcce32]">Contact Info</h4>
              <p className="text-gray-300 mb-2">82, Chauhan Market Rd, C Block</p>
              <p className="text-gray-300 mb-2">Sector 53, Noida, UP 201307</p>
              <p className="text-gray-300">Phone: 085888 24881</p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4 text-[#fcce32]">Quick Links</h4>
              <div className="space-y-2">
                <a href="/catalog" className="block text-gray-300 hover:text-[#fcce32] transition-colors">Products</a>
                <a href="/about" className="block text-gray-300 hover:text-[#fcce32] transition-colors">About Us</a>
                <a href="/contact" className="block text-gray-300 hover:text-[#fcce32] transition-colors">Contact Us</a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-8 text-center text-gray-400">
            <p>© 2025 Sai Food House. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Homepage;
